import type { CompanyResponse, UpdateCompanyDto } from '@super/shared-types'
import { BaseApiClient } from './base-client'
import { API_ROUTES, interpolateRoute } from '@super/shared-types'

export interface AvailablePhoneNumber {
  phoneNumber: string
  friendlyName: string
  locality: string | null
  region: string | null
  isoCountry: string
  capabilities: {
    voice: boolean
    sms: boolean
    mms: boolean
  }
}

export interface ListAvailableNumbersParams {
  areaCode?: string
  locality?: string
  contains?: string
}

/**
 * Company search parameters
 */
export interface CompanySearchParams {
  q?: string
  orgId?: string
  companyId?: string
  limit?: number
  offset?: number
}

/**
 * Company search response
 */
export interface CompanySearchResponse {
  companies: CompanyResponse[]
  total: number
  limit: number
  offset: number
}

/**
 * API response wrapper
 */
interface ApiResponse<T = any> {
  data: T
  success?: boolean
  message?: string
}

/**
 * Companies API Client
 * Handles all company-related API operations
 */
export class CompaniesClient extends BaseApiClient {
  /**
   * Get current user's company
   */
  async getCurrentCompany(): Promise<CompanyResponse> {
    const response = await this.request<ApiResponse<CompanyResponse>>(
      API_ROUTES.COMPANIES.GET,
    )
    return response.data
  }

  /**
   * Update company
   * @param companyId - The ID of the company to update
   * @param data - The data to update the company with
   * @returns The updated company
   */
  async updateCompany(
    companyId: string,
    data: UpdateCompanyDto,
  ): Promise<CompanyResponse> {
    const response = await this.request<ApiResponse<CompanyResponse>>(
      interpolateRoute(API_ROUTES.COMPANIES.UPDATE, { id: companyId }),
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    )
    return response.data
  }

  /**
   * Set (or clear) the company's listings fallback link — the URL texted to
   * callers when no matching listing can be offered. Pass an empty string to
   * clear it. Returns the saved value (or null when cleared).
   */
  async setListingsFallbackLink(
    companyId: string,
    listingsFallbackLink: string | null,
  ): Promise<string | null> {
    const response = await this.request<
      ApiResponse<{ listingsFallbackLink: string | null }>
    >(
      interpolateRoute(API_ROUTES.COMPANIES.LISTINGS_FALLBACK_LINK, {
        id: companyId,
      }),
      {
        method: 'PUT',
        body: JSON.stringify({ listingsFallbackLink }),
      },
    )
    return response.data?.listingsFallbackLink ?? null
  }

  /**
   * Set (or clear) a specific company's listings fallback link (Super Admin only).
   */
  async setAdminListingsFallbackLink(
    companyId: string,
    listingsFallbackLink: string | null,
  ): Promise<string | null> {
    const response = await this.request<
      ApiResponse<{ listingsFallbackLink: string | null }>
    >(
      interpolateRoute(API_ROUTES.ADMIN.COMPANIES.LISTINGS_FALLBACK_LINK, {
        id: companyId,
      }),
      {
        method: 'PUT',
        body: JSON.stringify({ listingsFallbackLink }),
      },
    )
    return response.data?.listingsFallbackLink ?? null
  }

  /**
   * Search companies (Super Data Admin only)
   * @param params - The companysearch parameters
   * @returns The search results of companies
   */
  async searchCompanies(
    params: CompanySearchParams = {},
  ): Promise<CompanySearchResponse> {
    const queryString = this.buildQueryString(params)
    const path = `${API_ROUTES.ADMIN.COMPANIES.SEARCH}${queryString}`

    const response =
      await this.request<ApiResponse<CompanySearchResponse>>(path)
    return response.data
  }

  /**
   * Get company by ID (Super Data Admin only)
   * @param id - The ID of the company to get
   * @returns The company with the given ID
   */
  async getCompanyById(id: string): Promise<CompanyResponse> {
    const response = await this.request<ApiResponse<CompanyResponse>>(
      interpolateRoute(API_ROUTES.ADMIN.COMPANIES.GET_BY_ID, { id }),
    )
    return response.data
  }

  /**
   * Update any company (Super Data Admin only)
   * @param companyId - The ID of the company to update
   * @param data - The data to update the company with
   * @returns The updated company
   */
  /**
   * List available Twilio phone numbers for the given company.
   */
  async listAvailablePhoneNumbers(
    companyId: string,
    params: ListAvailableNumbersParams = {},
  ): Promise<{ numbers: AvailablePhoneNumber[] }> {
    const queryString = this.buildQueryString(params)
    const path = `${interpolateRoute(API_ROUTES.COMPANIES.PHONE_AVAILABLE, {
      id: companyId,
    })}${queryString}`

    const response =
      await this.request<ApiResponse<{ numbers: AvailablePhoneNumber[] }>>(path)
    return response.data
  }

  /**
   * Provision a Twilio phone number for the given company.
   */
  async provisionPhoneNumber(
    companyId: string,
    phoneNumber: string,
  ): Promise<{ phoneNumber: string; sid: string }> {
    const response = await this.request<
      ApiResponse<{ phoneNumber: string; sid: string }>
    >(
      interpolateRoute(API_ROUTES.COMPANIES.PHONE_PROVISION, { id: companyId }),
      {
        method: 'POST',
        body: JSON.stringify({ phoneNumber }),
      },
    )
    return response.data
  }

  async updateCompanyAsAdmin(
    companyId: string,
    data: UpdateCompanyDto,
  ): Promise<CompanyResponse> {
    const response = await this.request<ApiResponse<CompanyResponse>>(
      interpolateRoute(API_ROUTES.ADMIN.COMPANIES.UPDATE, { id: companyId }),
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    )
    return response.data
  }
}

/**
 * Default companies client instance
 */
export const companiesClient = new CompaniesClient()
